import type {
  BrowserAuthExchangeResult,
  BrowserAuthProvider,
  BrowserAuthSession,
  BrowserPendingAuthState,
} from './browser-types'
import { emitBrowserMainStoreSet, ensureStoreSnapshotNamespace } from './browser-store'
import {
  generateSecureHex,
  getAuthApiBaseUrl,
  getStringField,
  isTokenExpiringSoon,
  parseJwtPayload,
  parseBooleanFlag,
} from './browser-utils'

const AUTH_TOKEN_KEY = 'highlight:auth-token'
const AUTH_SESSION_KEY = 'highlight:auth-session'
const AUTH_LAST_SIGNIN_URL_KEY = 'highlight:auth-last-signin-url'
const AUTH_PENDING_STATE_KEY = 'highlight:auth-pending-state'
const AUTH_CALLBACK_STATE_QUERY_PARAM = 'hl_auth_state'
const AUTH_PENDING_STATE_TTL_MS = 10 * 60 * 1000
const REFRESH_UNAUTHORIZED_ERROR = 'browser-auth-refresh-unauthorized'

let authBootstrapPromise: Promise<void> | null = null

// --- Internal helpers ---

const getLastSignInUrl = (): string | null => {
  try {
    return localStorage.getItem(AUTH_LAST_SIGNIN_URL_KEY)
  } catch (error) {
    console.warn('[browser-runtime] Failed to read last sign-in URL', error)
    return null
  }
}

const setLastSignInUrl = (url: string): void => {
  try {
    localStorage.setItem(AUTH_LAST_SIGNIN_URL_KEY, url)
  } catch (error) {
    console.warn('[browser-runtime] Failed to persist last sign-in URL', error)
  }
}

const readPendingAuthState = (): BrowserPendingAuthState | null => {
  try {
    const rawValue = localStorage.getItem(AUTH_PENDING_STATE_KEY)
    if (!rawValue) {
      return null
    }
    const parsed = JSON.parse(rawValue) as BrowserPendingAuthState
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.value !== 'string' ||
      parsed.value.length === 0 ||
      typeof parsed.createdAt !== 'number'
    ) {
      return null
    }
    return parsed
  } catch (error) {
    console.warn('[browser-runtime] Failed to read pending auth state', error)
    return null
  }
}

const persistPendingAuthState = (state: string): void => {
  try {
    localStorage.setItem(
      AUTH_PENDING_STATE_KEY,
      JSON.stringify({
        value: state,
        createdAt: Date.now(),
      } satisfies BrowserPendingAuthState),
    )
  } catch (error) {
    console.warn('[browser-runtime] Failed to persist pending auth state', error)
  }
}

const clearPendingAuthState = (): void => {
  try {
    localStorage.removeItem(AUTH_PENDING_STATE_KEY)
  } catch (error) {
    console.warn('[browser-runtime] Failed to clear pending auth state', error)
  }
}

const validatePendingAuthState = (
  callbackState: string | null,
  options?: { allowMissingCallbackState?: boolean },
): { valid: true; message?: string } | { valid: false; message: string } => {
  const pendingState = readPendingAuthState()
  if (!pendingState) {
    return { valid: false, message: 'no pending auth state' }
  }

  if (Date.now() - pendingState.createdAt > AUTH_PENDING_STATE_TTL_MS) {
    clearPendingAuthState()
    return { valid: false, message: 'pending auth state expired' }
  }

  if (!callbackState) {
    if (options?.allowMissingCallbackState) {
      return { valid: true }
    }
    return { valid: false, message: 'missing callback state' }
  }

  if (callbackState !== pendingState.value) {
    return { valid: false, message: 'state mismatch' }
  }

  return { valid: true }
}

// --- Session persistence ---

export const readAuthSession = (): BrowserAuthSession | null => {
  try {
    const rawSession = localStorage.getItem(AUTH_SESSION_KEY)
    if (!rawSession) {
      return null
    }

    const parsed = JSON.parse(rawSession) as BrowserAuthSession
    if (
      !parsed ||
      (parsed.provider !== 'workos' && parsed.provider !== 'supabase') ||
      typeof parsed.accessToken !== 'string' ||
      parsed.accessToken.length === 0
    ) {
      return null
    }

    return parsed
  } catch (error) {
    console.warn('[browser-runtime] Failed to read auth session from localStorage', error)
    return null
  }
}

const synchronizeAuthStateWithStores = (session: BrowserAuthSession | null): void => {
  const existingUser = ensureStoreSnapshotNamespace('global.state').user as Record<string, unknown> | undefined
  const tokenUser = session ? getUserFromAccessToken(session.accessToken, session.provider) : undefined
  const user = session
    ? (tokenUser ??
      (existingUser
        ? {
            ...existingUser,
            isAnonymous: false,
            provider: session.provider ?? (existingUser.provider as string) ?? 'workos',
          }
        : undefined))
    : undefined

  const globalStateSnapshot = ensureStoreSnapshotNamespace('global.state')
  globalStateSnapshot.user = user
  const userStoreSnapshot = ensureStoreSnapshotNamespace('user.store')
  if (user?.email) {
    userStoreSnapshot.profileEmail = user.email
  }

  emitBrowserMainStoreSet('global.state', 'user', user)
  emitBrowserMainStoreSet('user.store', 'profileEmail', user?.email)
}

const persistAuthSession = (session: BrowserAuthSession | null): void => {
  try {
    if (!session) {
      localStorage.removeItem(AUTH_SESSION_KEY)
      localStorage.removeItem(AUTH_TOKEN_KEY)
      synchronizeAuthStateWithStores(null)
      return
    }

    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session))
    localStorage.setItem(AUTH_TOKEN_KEY, session.accessToken)
    synchronizeAuthStateWithStores(session)
  } catch (error) {
    console.warn('[browser-runtime] Failed to persist auth session', error)
  }
}

// --- Token helpers ---

export const getAuthToken = (): string => {
  const envToken = import.meta.env.VITE_HIGHLIGHT_AUTH_TOKEN
  if (typeof envToken === 'string' && envToken.length > 0) {
    return envToken
  }

  const session = readAuthSession()
  if (session?.accessToken) {
    return session.accessToken
  }

  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) ?? ''
  } catch (error) {
    console.warn('[browser-runtime] Failed to access auth token from localStorage', error)
    return ''
  }
}

export const getAuthProvider = (): BrowserAuthProvider => {
  if (parseBooleanFlag(import.meta.env.VITE_HIGHLIGHT_DISABLE_AUTH)) {
    return 'workos'
  }

  const envProvider = import.meta.env.VITE_HIGHLIGHT_AUTH_PROVIDER
  if (envProvider === 'workos' || envProvider === 'supabase') {
    return envProvider
  }

  const session = readAuthSession()
  if (session?.provider) {
    return session.provider
  }

  return getAuthToken().length > 0 ? 'workos' : 'supabase'
}

export const getUserFromAccessToken = (
  accessToken: string,
  provider: BrowserAuthProvider,
): Record<string, unknown> | undefined => {
  const payload = parseJwtPayload(accessToken)
  if (!payload) {
    return undefined
  }

  const userId = typeof payload.sub === 'string' ? payload.sub : undefined
  if (!userId) {
    return undefined
  }

  const email = typeof payload.email === 'string' ? payload.email : undefined

  // Org-scoped session (AuthKit account switcher): the token carries the selected org and the
  // caller's role in it. Absent on user-scoped sessions.
  const organizationId = typeof payload.org_id === 'string' ? payload.org_id : undefined
  const organizationRole = typeof payload.role === 'string' ? payload.role : undefined

  return {
    id: userId,
    email,
    isAnonymous: false,
    provider,
    organizationId,
    organizationRole,
  }
}

export const extractUserFromProfilePayload = (profilePayload: unknown): Record<string, unknown> | undefined => {
  if (!profilePayload || typeof profilePayload !== 'object') {
    return undefined
  }

  const root = profilePayload as Record<string, unknown>
  const nestedUser = root.user && typeof root.user === 'object' ? (root.user as Record<string, unknown>) : undefined
  const source = nestedUser ?? root

  const id =
    getStringField(source.id) ??
    getStringField(source.userId) ??
    getStringField(source.sub) ??
    getStringField(root.id) ??
    getStringField(root.userId) ??
    getStringField(root.sub)
  if (!id) {
    return undefined
  }

  return {
    id,
    email: getStringField(source.email) ?? getStringField(root.email),
    firstName: getStringField(source.firstName) ?? getStringField(root.firstName),
    lastName: getStringField(source.lastName) ?? getStringField(root.lastName),
    isAnonymous: false,
    provider: getStringField(source.provider) ?? getStringField(root.provider) ?? 'workos',
  }
}

// --- Auth session response parsing ---

const extractSessionFromAuthResponse = (
  payload: unknown,
  fallbackProvider: BrowserAuthProvider = 'workos',
): BrowserAuthSession | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const responsePayload = payload as { data?: { accessToken?: string; refreshToken?: string } }
  const accessToken = responsePayload.data?.accessToken
  if (!accessToken || typeof accessToken !== 'string') {
    return null
  }

  const refreshToken =
    typeof responsePayload.data?.refreshToken === 'string' ? responsePayload.data.refreshToken : undefined

  return {
    provider: fallbackProvider,
    accessToken,
    refreshToken,
  }
}

const requestBrowserAuthTokenPair = async (
  endpoint: string,
  body: Record<string, unknown>,
  fallbackProvider: BrowserAuthProvider = 'workos',
  options?: { throwOnUnauthorized?: boolean; accessToken?: string },
): Promise<BrowserAuthSession | null> => {
  try {
    const response = await fetch(new URL(endpoint, getAuthApiBaseUrl()), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Some auth endpoints (switch-org) require a Bearer identity in addition to the
        // refresh token in the body — the gateway's flag gate needs it before consuming
        // the single-use refresh token.
        ...(options?.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}),
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const responseText = await response.text()
      console.error(`[browser-runtime] Auth request failed (${endpoint})`, response.status, responseText)
      if (options?.throwOnUnauthorized && response.status === 401) {
        throw new Error(REFRESH_UNAUTHORIZED_ERROR)
      }
      return null
    }

    const responseBody = await response.json()
    const session = extractSessionFromAuthResponse(responseBody, fallbackProvider)
    if (!session) {
      console.warn(`[browser-runtime] Auth response missing token data (${endpoint})`)
      return null
    }

    persistAuthSession(session)
    return session
  } catch (error) {
    if (error instanceof Error && error.message === REFRESH_UNAUTHORIZED_ERROR) {
      throw error
    }
    console.error(`[browser-runtime] Failed auth request (${endpoint})`, error)
    return null
  }
}

// --- Token refresh ---

const refreshBrowserAuthSession = async (): Promise<BrowserAuthSession | null> => {
  try {
    const envToken = import.meta.env.VITE_HIGHLIGHT_AUTH_TOKEN
    if (typeof envToken === 'string' && envToken.length > 0) {
      return {
        provider: 'workos',
        accessToken: envToken,
      }
    }

    const currentSession = readAuthSession()
    if (!currentSession?.accessToken) {
      return null
    }

    if (!isTokenExpiringSoon(currentSession.accessToken)) {
      return currentSession
    }

    if (!currentSession.refreshToken) {
      return currentSession
    }

    const refreshedSession = await requestBrowserAuthTokenPair(
      '/api/v1/auth/refresh',
      { refreshToken: currentSession.refreshToken },
      currentSession.provider,
      { throwOnUnauthorized: true },
    )

    if (!refreshedSession) {
      console.warn('[browser-runtime] Token refresh failed, keeping existing session')
      return currentSession
    }

    return refreshedSession
  } catch (error) {
    console.error('[browser-runtime] Failed to refresh browser auth session, signing out', error)
    void browserSignOut()
    return null
  }
}

export const getBrowserAuthAccessToken = async (): Promise<string> => {
  try {
    const session = await refreshBrowserAuthSession()
    return session?.accessToken ?? ''
  } catch (error) {
    console.error('[browser-runtime] Failed to resolve auth access token', error)
    return ''
  }
}

// --- Sign-in URL construction ---

const createBrowserSignInUrl = (screenHint?: 'sign-in' | 'sign-up'): string => {
  const callbackState = generateSecureHex(16)
  persistPendingAuthState(callbackState)

  const currentUrl = new URL(window.location.href)
  const callbackUrl = new URL(currentUrl.pathname, currentUrl.origin)

  const signInUrl = new URL('/api/v1/auth/signin', getAuthApiBaseUrl())
  signInUrl.searchParams.set('destination', 'browser')
  signInUrl.searchParams.set('redirect_to', callbackUrl.toString())
  if (screenHint) {
    signInUrl.searchParams.set('screenHint', screenHint)
  }
  const signInUrlString = signInUrl.toString()
  setLastSignInUrl(signInUrlString)
  return signInUrlString
}

export const getBrowserSignInUrl = (screenHint?: 'sign-in' | 'sign-up'): string => {
  if (screenHint === 'sign-up') {
    return createBrowserSignInUrl('sign-up')
  }
  const existingUrl = getLastSignInUrl()
  if (existingUrl) {
    const pendingState = readPendingAuthState()
    if (pendingState && Date.now() - pendingState.createdAt <= AUTH_PENDING_STATE_TTL_MS) {
      return existingUrl
    }
  }
  return createBrowserSignInUrl(screenHint)
}

// --- URL callback handling ---

const clearBrowserAuthParamsFromLocation = (): void => {
  try {
    const url = new URL(window.location.href)
    const hadParams = ['code', 'state', 'error', 'error_description', AUTH_CALLBACK_STATE_QUERY_PARAM].some((key) =>
      url.searchParams.has(key),
    )
    if (!hadParams) {
      return
    }

    url.searchParams.delete('code')
    url.searchParams.delete('state')
    url.searchParams.delete('error')
    url.searchParams.delete('error_description')
    url.searchParams.delete(AUTH_CALLBACK_STATE_QUERY_PARAM)
    window.history.replaceState({}, document.title, url.toString())
  } catch (error) {
    console.warn('[browser-runtime] Failed to clear auth params from URL', error)
  }
}

const bootstrapBrowserAuthFromLocation = async (): Promise<void> => {
  let shouldClearParams = false
  try {
    const url = new URL(window.location.href)
    const authCode = url.searchParams.get('code')
    if (!authCode) {
      if (url.searchParams.has('error') || url.searchParams.has('error_description')) {
        shouldClearParams = true
      }
      return
    }

    const callbackState = url.searchParams.get(AUTH_CALLBACK_STATE_QUERY_PARAM)
    const stateValidation = validatePendingAuthState(callbackState, { allowMissingCallbackState: true })
    if (!stateValidation.valid) {
      shouldClearParams = true
      console.warn('[browser-runtime] Ignoring auth callback due to invalid state', stateValidation.message)
      return
    }

    const authSession = await requestBrowserAuthTokenPair('/api/v1/auth/exchange', { code: authCode }, 'workos')
    if (authSession?.accessToken) {
      shouldClearParams = true
      clearPendingAuthState()
    } else {
      console.warn(
        '[browser-runtime] Auth code exchange failed; keeping callback params so user can retry or manually paste the URL/code.',
      )
    }
  } catch (error) {
    console.error('[browser-runtime] Failed to bootstrap auth from URL', error)
  } finally {
    if (shouldClearParams) {
      clearBrowserAuthParamsFromLocation()
    }
  }
}

// --- Profile sync ---

export const browserGetProfile = async (): Promise<unknown> => {
  try {
    const accessToken = await getBrowserAuthAccessToken()
    if (!accessToken) {
      return undefined
    }

    const response = await fetch(new URL('/api/v1/auth/profile', getAuthApiBaseUrl()), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      return undefined
    }

    const payload = await response.json()
    if (payload && typeof payload === 'object' && 'data' in payload) {
      return (payload as { data?: unknown }).data
    }

    return payload
  } catch (error) {
    console.error('[browser-runtime] Failed to fetch auth profile', error)
    return undefined
  }
}

const syncBrowserUserFromProfile = async (): Promise<void> => {
  try {
    const session = readAuthSession()
    if (!session?.accessToken) {
      return
    }

    const tokenUser = getUserFromAccessToken(session.accessToken, session.provider)
    const profilePayload = await browserGetProfile()
    const profileUser = extractUserFromProfilePayload(profilePayload)
    const mergedUser: Record<string, unknown> = {
      ...(tokenUser ?? {}),
      ...(profileUser ?? {}),
      isAnonymous: false,
      provider: session.provider,
    }

    if (!mergedUser.id) {
      return
    }

    const globalStateSnapshot = ensureStoreSnapshotNamespace('global.state')
    globalStateSnapshot.user = mergedUser
    const userStoreSnapshot = ensureStoreSnapshotNamespace('user.store')
    if (mergedUser.email) {
      userStoreSnapshot.profileEmail = mergedUser.email
    }

    emitBrowserMainStoreSet('global.state', 'user', mergedUser)
    emitBrowserMainStoreSet('user.store', 'profileEmail', mergedUser.email)
  } catch (error) {
    console.error('[browser-runtime] Failed to sync user profile', error)
  }
}

// --- Organization switching (AuthKit account switcher) ---

export interface BrowserSwitchableOrg {
  organizationId: string
  name: string
  role: string | null
  isPersonal: boolean
}

/**
 * The orgs the signed-in user can switch between (personal org + shared orgs), plus the org the current
 * session is scoped to. Backs an in-app account switcher; the hosted AuthKit switcher already
 * handles org selection at sign-in.
 */
export const fetchBrowserSwitchableOrgs = async (): Promise<{
  currentOrganizationId: string | null
  organizations: Array<BrowserSwitchableOrg>
}> => {
  const empty = { currentOrganizationId: null, organizations: [] as Array<BrowserSwitchableOrg> }
  try {
    const accessToken = await getBrowserAuthAccessToken()
    if (!accessToken) {
      return empty
    }

    const response = await fetch(new URL('/api/v1/auth/organizations', getAuthApiBaseUrl()), {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!response.ok) {
      return empty
    }

    const payload = (await response.json()) as {
      data?: { currentOrganizationId?: string | null; organizations?: Array<BrowserSwitchableOrg> }
    }
    return {
      currentOrganizationId: payload.data?.currentOrganizationId ?? null,
      organizations: payload.data?.organizations ?? [],
    }
  } catch (error) {
    console.error('[browser-runtime] Failed to fetch switchable organizations', error)
    return empty
  }
}

/**
 * Switch the active org without a full re-login: re-mints the session against `organizationId` using
 * the current refresh token and persists it (which re-syncs the user, now carrying the new org_id).
 * Returns null if there's no session — the caller should fall back to a full sign-in.
 *
 * The gateway requires a Bearer identity on switch-org in addition to the refresh token, so a fresh
 * access token is fetched FIRST — which also runs any pending auto-refresh before we read the
 * session, so the refresh token we send is the freshest one (narrowing the single-use-token race
 * with the background refresh path).
 */
export const switchBrowserAuthOrganization = async (organizationId: string): Promise<BrowserAuthSession | null> => {
  const accessToken = await getBrowserAuthAccessToken()
  if (!accessToken) {
    console.warn('[browser-runtime] Cannot switch organization without a session')
    return null
  }

  const currentSession = readAuthSession()
  if (!currentSession?.refreshToken) {
    console.warn('[browser-runtime] Cannot switch organization without a refresh token')
    return null
  }

  return requestBrowserAuthTokenPair(
    '/api/v1/auth/switch-org',
    { refreshToken: currentSession.refreshToken, organizationId },
    currentSession.provider,
    { accessToken },
  )
}

// --- Bootstrap promise ---

export const ensureBrowserAuthBootstrap = async (): Promise<void> => {
  if (!authBootstrapPromise) {
    authBootstrapPromise = (async () => {
      await bootstrapBrowserAuthFromLocation()
      await syncBrowserUserFromProfile()
    })()
  }
  try {
    await authBootstrapPromise
  } catch (error) {
    console.error('[browser-runtime] Browser auth bootstrap failed', error)
  }
}

// --- Initial user for store snapshot ---

export const getInitialBrowserUser = (disableAuth: boolean): Record<string, unknown> | undefined => {
  if (disableAuth) {
    return {
      id: import.meta.env.VITE_HIGHLIGHT_BROWSER_USER_ID ?? 'browser-user',
      email: import.meta.env.VITE_HIGHLIGHT_BROWSER_USER_EMAIL ?? 'browser@highlight.local',
      firstName: import.meta.env.VITE_HIGHLIGHT_BROWSER_USER_FIRST_NAME ?? 'Browser',
      lastName: import.meta.env.VITE_HIGHLIGHT_BROWSER_USER_LAST_NAME ?? 'User',
      isAnonymous: false,
      provider: 'workos',
    }
  }

  const accessToken = getAuthToken()
  if (!accessToken) {
    return undefined
  }

  return getUserFromAccessToken(accessToken, getAuthProvider())
}

// --- Sign-in / sign-out flows ---

const extractAuthExchangeInput = (input: string): { authCode: string | null; callbackState: string | null } => {
  const rawValue = input.trim()
  if (!rawValue) {
    return { authCode: null, callbackState: null }
  }

  try {
    if (rawValue.startsWith('http://') || rawValue.startsWith('https://')) {
      const parsedUrl = new URL(rawValue)
      const urlCode = parsedUrl.searchParams.get('code')
      const callbackState = parsedUrl.searchParams.get(AUTH_CALLBACK_STATE_QUERY_PARAM)
      if (urlCode && urlCode.trim().length > 0) {
        return {
          authCode: urlCode.trim(),
          callbackState: callbackState && callbackState.trim().length > 0 ? callbackState.trim() : null,
        }
      }
    }
  } catch (error) {
    console.warn('[browser-runtime] Failed to parse pasted URL for auth code', error)
  }

  const queryCodeMatch = rawValue.match(/[?&#]code=([^&#\s]+)/)
  const queryStateMatch = rawValue.match(new RegExp(`[?&#]${AUTH_CALLBACK_STATE_QUERY_PARAM}=([^&#\\s]+)`))
  const callbackState = queryStateMatch?.[1]
    ? (() => {
        try {
          return decodeURIComponent(queryStateMatch[1]).trim()
        } catch (error) {
          console.warn('[browser-runtime] Failed to decode callback state from pasted value', error)
          return queryStateMatch[1].trim()
        }
      })()
    : null
  if (queryCodeMatch?.[1]) {
    try {
      return {
        authCode: decodeURIComponent(queryCodeMatch[1]).trim(),
        callbackState,
      }
    } catch (error) {
      console.warn('[browser-runtime] Failed to decode auth code from pasted value', error)
      return {
        authCode: queryCodeMatch[1].trim(),
        callbackState,
      }
    }
  }

  if (!rawValue.includes(' ') && rawValue.length >= 12) {
    return {
      authCode: rawValue,
      callbackState,
    }
  }

  return { authCode: null, callbackState }
}

export const exchangeManualAuthInput = async (input: string): Promise<BrowserAuthExchangeResult> => {
  try {
    const { authCode, callbackState } = extractAuthExchangeInput(input)
    if (!authCode) {
      return {
        success: false,
        message: 'Could not find a valid code in that value.',
      }
    }

    const stateValidation = validatePendingAuthState(callbackState, { allowMissingCallbackState: true })
    if (!stateValidation.valid) {
      return {
        success: false,
        message: stateValidation.message,
      }
    }

    const authSession = await requestBrowserAuthTokenPair('/api/v1/auth/exchange', { code: authCode }, 'workos')
    if (!authSession?.accessToken) {
      return {
        success: false,
        message: 'Auth exchange failed. Please retry with a fresh deeplink URL/code.',
      }
    }

    clearPendingAuthState()
    return {
      success: true,
      message: 'Signed in successfully.',
    }
  } catch (error) {
    console.error('[browser-runtime] Manual auth input exchange failed', error)
    return {
      success: false,
      message: 'Auth exchange failed unexpectedly.',
    }
  }
}

export const browserShowSignIn = async (screenHint?: 'sign-in' | 'sign-up'): Promise<void> => {
  try {
    const signInUrl = createBrowserSignInUrl(screenHint)
    window.location.assign(signInUrl)
  } catch (error) {
    console.error('[browser-runtime] Failed to open sign-in URL', error)
  }
}

export const browserShowSignOut = async (): Promise<void> => {
  try {
    await browserSignOut()
    window.location.assign(createBrowserSignInUrl('sign-in'))
  } catch (error) {
    console.error('[browser-runtime] Failed to open sign-out URL', error)
  }
}

export const browserSignOut = async (): Promise<void> => {
  try {
    const currentSession = readAuthSession()
    const accessToken = currentSession?.accessToken

    persistAuthSession(null)

    if (accessToken) {
      fetch(new URL('/api/v1/auth/signout', getAuthApiBaseUrl()), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).catch((error: unknown) => {
        console.warn('[browser-runtime] Failed to call signout endpoint', error)
      })
    }
  } catch (error) {
    console.error('[browser-runtime] Failed to sign out', error)
    persistAuthSession(null)
  }
}

export const browserGetConnectUrlForProvider = async (
  provider: string,
  options?: { connectionId?: string },
): Promise<string> => {
  if (!provider) {
    return '#'
  }
  const fallbackUrl = new URL(`/api/v1/connect/${provider}`, getAuthApiBaseUrl())
  if (options?.connectionId) {
    fallbackUrl.searchParams.set('connectionId', options.connectionId)
  }
  try {
    await ensureBrowserAuthBootstrap()
    const accessToken = await getBrowserAuthAccessToken()
    if (!accessToken) {
      return fallbackUrl.toString()
    }

    const response = await fetch(fallbackUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      redirect: 'manual',
    })

    return response.headers.get('Location') ?? fallbackUrl.toString()
  } catch (error) {
    console.warn('[browser-runtime] Failed to fetch connect URL for provider', provider, error)
    return fallbackUrl.toString()
  }
}

export const isAutoLoginNeeded = (): boolean => {
  if (parseBooleanFlag(import.meta.env.VITE_HIGHLIGHT_DISABLE_AUTH)) {
    return false
  }

  // A token supplied by the environment is already a signed-in state, and
  // `getAuthToken` prefers it over everything else. Without this the redirect
  // fires anyway, and since the OAuth callback is registered for exactly one
  // origin, browser mode can only ever be authed on that single port.
  if (typeof import.meta.env.VITE_HIGHLIGHT_AUTH_TOKEN === 'string' && import.meta.env.VITE_HIGHLIGHT_AUTH_TOKEN) {
    return false
  }

  const session = readAuthSession()
  if (session?.accessToken) {
    return false
  }

  const url = new URL(window.location.href)
  if (url.searchParams.has('code')) {
    return false
  }

  const pendingState = readPendingAuthState()
  if (pendingState && Date.now() - pendingState.createdAt <= AUTH_PENDING_STATE_TTL_MS) {
    return false
  }

  return true
}

export const ensureBrowserAuthHelpers = (): void => {
  try {
    window.highlightBrowserAuth = {
      isBrowserMode: true,
      getSignInUrl: (screenHint?: 'sign-in' | 'sign-up') => getBrowserSignInUrl(screenHint),
      getLastSignInUrl: () => getLastSignInUrl(),
      openSignIn: async (screenHint?: 'sign-in' | 'sign-up') => {
        await browserShowSignIn(screenHint)
      },
      exchangeAuthCode: async (value: string) => {
        return await exchangeManualAuthInput(value)
      },
    }
  } catch (error) {
    console.warn('[browser-runtime] Failed to expose browser auth helpers', error)
  }
}
